<script lang="ts">
  import { onMount } from "svelte"
  import { fade } from "svelte/transition"
  import { flip } from "svelte/animate"
  import { xhr, toast, changeIconClass, clapIcon } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Button from "$app/components/ButtonSpinner.svelte"
  import LoadingSpinner from "$app/components/LoadingSpinner.svelte"
  import ExternalRecipient from "./ExternalRecipient.svelte"
  import RecipientSelect from "./RecipientSelect.svelte"
  import { Plus } from "lucide-svelte"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { appStore } from "$app/store.svelte"

  let props = $props()

  let { plan_id, oncreated, onupdated, edit = false, recognition = {} } = props

  let users: UserType[] = $state([])
  let externalRecipients: ExternalRecipientType[] = $state([])
  let recipients: (UserType | ExternalRecipientType)[] = $state([])
  let statements: StatementType[] = $state([])
  let errors: { [key: string]: string } = $state({})
  let isLoading = $state(false)

  let comment = $state(recognition.comment || "")
  let userFilter = $state("")
  let resendEmail = $state(false)

  let addExternalRecipient = $state(false)

  function toggleUserSelection(user) {
    user._selected = !user._selected
    const userIndex = recipients.findIndex((obj) => obj.id === user.id)
    if (userIndex > -1) {
      recipients[userIndex] = user
    }
    recipients = recipients
  }

  let filteredUsers: (UserType | ExternalRecipientType)[] = $state([])
  let selectedUsers: (UserType | ExternalRecipientType)[] = $state([])

  const filterBy = (user: UserType | ExternalRecipientType) => {
    if (!user._selected) {
      return user.name.toLowerCase().includes(userFilter.toLowerCase())
    }
  }

  onMount(async () => {
    isLoading = true
    initializeForm()
  })

  type RespType = {
    users: typeof users
    external_recipients: typeof externalRecipients
    statements: typeof statements
  }
  // Get all statements for this section
  async function initializeForm(): Promise<void> {
    errors = {}
    addExternalRecipient = false
    isLoading = true
    try {
      const resp: Response<RespType, RespType[]> = await xhr.get(
        `/plans/${plan_id}/recognitions/new.json`
      )

      isLoading = false
      users = resp.data.users
      externalRecipients = resp.data.external_recipients
      statements = resp.data.statements

      users = users.map((u) => {
        return { ...u, _selected: false, external: false }
      })
      externalRecipients = externalRecipients.map((u) => {
        return { ...u, _selected: false, external: true }
      })
      recipients = [...users, ...externalRecipients]

      if (recognition.id) setEditData()
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading a new recognition", "error")
    } finally {
      isLoading = false
    }
    addExternalRecipient = false
  }

  function setEditData(): void {
    // recipients
    recognition.recipients.forEach((r) => {
      const recipient = recipients.find((u) => u.id === r.id)
      if (recipient) recipient._selected = true
    })
    // statements
    recognition.statements.forEach((s) => {
      const statement = statements.find((st) => st.id === s.id)
      if (statement) statement._selected = true
    })

    recipients = recipients
  }

  // Send Recognition
  let savingRecognition = $state(false)
  async function sendRecognition(): Promise<void> {
    savingRecognition = true

    let userIds = users.filter((u) => u._selected).map((u) => u.id)
    let externalRecipientIds = externalRecipients
      .filter((u) => u._selected)
      .map((u) => u.id)
    let statementIds = statements.filter((s) => s._selected).map((s) => s.id)
    try {
      const resp = await xhr.post(`/plans/${plan_id}/recognitions.json`, {
        recognition: {
          user_ids: userIds,
          external_recipient_ids: externalRecipientIds,
          statement_ids: statementIds,
          comment: comment,
          created_at: new Date().toUTCString()
        }
      })

      appStore.recognitions = [resp.data, ...appStore.recognitions]
      oncreated(resp.data)
      toast("Recognition sent successfully", "success")
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      errors = e.response.data
      toast("There was an error sending the recognition", "error")
    } finally {
      savingRecognition = false
    }
  }

  async function updateRecognition(): Promise<void> {
    savingRecognition = true

    let userIds = users.filter((u) => u._selected).map((u) => u.id)
    let externalRecipientIds = externalRecipients
      .filter((u) => u._selected)
      .map((u) => u.id)
    let statementIds = statements.filter((s) => s._selected).map((s) => s.id)
    try {
      const resp = await xhr.patch(
        `/plans/${plan_id}/recognitions/${recognition.id}.json`,
        {
          recognition: {
            user_ids: userIds,
            external_recipient_ids: externalRecipientIds,
            statement_ids: statementIds,
            comment: comment,
            resend_email: resendEmail
          }
        }
      )
      const idx = appStore.recognitions.findIndex(
        (r) => r.id === recognition.id
      )
      appStore.recognitions[idx] = resp.data
      onupdated(resp.data)
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      errors = e.response.data
      toast("There was an error sending the recognition", "error")
    } finally {
      savingRecognition = false
    }
  }

  $effect(() => {
    selectedUsers = recipients.filter((u) => u._selected)

    filteredUsers =
      userFilter === ""
        ? recipients.sort((a, b) => b._selected - a._selected)
        : recipients.filter((user) => filterBy(user))
  })
</script>

<div class="mt-3 sm:mt-5 overflow-visible" data-name="recognition-form">
  <div class="flex justify-center w-full items-center">
    {@html changeIconClass(clapIcon, "mr-4 w-10 h-10")}
    <h3
      class="text-3xl leading-6 font-medium text-gray-900"
      id="modal-headline"
    >
      Send a Recognition
    </h3>
  </div>
  <div class="mt-8">
    {#if isLoading}
      <LoadingSpinner />
    {:else}
      <div class="flex flex-col w-full relative">
        {#if addExternalRecipient}
          <div class="fixed inset-0"></div>
          <div class="fixed inset-0 my-6 overflow-hidden z-10" transition:fade>
            <ExternalRecipient
              {plan_id}
              onclose={() => (addExternalRecipient = false)}
              oncreated={() => initializeForm()}
            />
          </div>
        {/if}
        <div class="w-full flex items-center justify-between">
          <div class="flex justify-start flex-grow items-center">
            <p class="mr-4 font-bold whitespace">Select team member(s)</p>
            <input
              type="search"
              bind:value={userFilter}
              placeholder="Filter by Name"
              autocomplete="off"
              class="max-w-md block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border border-gray-300 rounded-md p-2"
            />
          </div>
          {#if ["leader", "admin"].includes(appStore.current_user.role)}
            <button
              class="btn outline sm flex items-center"
              onclick={() => (addExternalRecipient = true)}
            >
              <Plus class="size-4" />
              External Recipient</button
            >
          {/if}
        </div>

        <div
          class="grid grid-flow-col auto-cols-max grid-rows-1 full mt-2 checkbox-button overflow-x-scroll items-center"
          onwheel={(e) => {
            if (e.deltaMode === 0) {
              e.currentTarget.scrollLeft += e.deltaY
            }
          }}
        >
          {#if userFilter !== ""}
            {#each selectedUsers as user (user.id)}
              <div transition:fade>
                <RecipientSelect
                  {user}
                  onupdate={() => toggleUserSelection(user)}
                />
              </div>
            {/each}
          {/if}
          {#each filteredUsers as user (user.id)}
            <div animate:flip={{ duration: 500 }}>
              <RecipientSelect
                {user}
                onupdate={() => toggleUserSelection(user)}
              />
            </div>
          {:else}
            {#if selectedUsers.length === 0}
              <div
                class="m-2 py-3 px-12 text-lg font-bold text-gray-700 border rounded-lg border-gray-200 shadow-sm"
              >
                No users found matching search
              </div>
            {/if}
          {/each}
        </div>

        {#if errors.user_ids}
          <div class="text-red-500 text-sm">
            You must select at least one user
          </div>
        {/if}

        <div class="w-full justify-start mt-8">
          <p class="text-left font-bold">What do you recognize?</p>
          <div
            class="grid grid-cols-2 sm:flex sm:w-full sm:flex-wrap justify-center items-center px-4"
          >
            {#each statements as statement (statement.id)}
              <button
                class="mt-2 mx-2"
                data-name="recipient-statement"
                onclick={() => (statement._selected = !statement._selected)}
              >
                <span
                  class="bg-fwp-pale-grey rounded-full flex items-center select-none py-1 px-3 border border-gray-100 shadow-sm {statement._selected
                    ? 'bg-blue-500 text-white hover:border-blue-600'
                    : 'hover:bg-gray-100 hover:border-blue-300'}"
                >
                  <img
                    class="w-8 h-8 rounded-sm mx-auto"
                    src={statement.symbol_url}
                    alt={statement.name}
                  />
                  <span class="pl-2 text-xs">{statement.name}</span>
                </span>
              </button>
            {/each}
          </div>
        </div>

        {#if errors.statements}
          <div class="text-red-500 text-sm">
            You must select at least one recognition
          </div>
        {/if}
        <div
          class="w-full justify-start mt-8 input-cont"
          class:error={errors.comment}
        >
          <label class="font-bold" for="new-recognition-comment">
            Send a Message
          </label>
          <div class="mt-1">
            <Tiptap
              id="new-recognition-comment"
              placeholder="What do you recognize?"
              content={recognition.comment || ""}
              saving={false}
              onupdate={(det) => (comment = det.html)}
              allowEmojis={true}
              allowGiphy={true}
              buttons={[
                ["bold", "italic", "underline", "strike"],
                ["bulletList", "orderedList", "blockquote"],
                ["link", "unlink", "image"]
              ]}
            />
          </div>
          <div class="msg">{errors.comment}</div>
        </div>
        <div
          class="col-span-6 py-2 text-right mt-6 flex justify-end items-center"
        >
          {#if recognition?.id}
            <label
              class="mr-4 px-4 py-3 flex items-center text-base border rounded-sm border-gray-100"
            >
              <input
                type="checkbox"
                bind:checked={resendEmail}
                class="mr-2"
              />Resend email
            </label>
          {/if}
          <Button
            class="btn btn-rounded success btn-big"
            processing={savingRecognition}
            onclick={() => {
              recognition?.id ? updateRecognition() : sendRecognition()
            }}
          >
            {edit ? "Update Recognition" : "Send Recognition"}
          </Button>
        </div>
      </div>
    {/if}
  </div>
</div>
